import React, { FC, lazy, Suspense, useEffect, useState } from "react";
import { inject, observer, Provider } from "mobx-react";
import { Routes, Route, Navigate, NavigateFunction } from "react-router-dom";
import {
  AuthService,
  Loader,
  NoPermissionDialog,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import {
  CheckOutStore,
  CountryStore,
  ItemsStore,
  PurchaseStore,
  RolesAndPermissionStore,
} from "../shared/store";
import { DashboardMain, DashboardStore } from "./dashboard";
import { MerchantAccount } from "../shared/models";
import { MerchantAccountService } from "../shared/services";
import { ApplicationStatus } from "../shared/enums";
import {
  CheckoutDonation,
  CheckoutPreview,
  CheckoutPreviewForDetailPage,
  CheckOutRoutes,
  CheckoutsMain,
} from "./checkouts";
import { getMerchantAccountStore } from "./merchantAccounts/MerchantAccountStore";
import RootStore from "./RootStore";

const DisputesRoutes = lazy(() => import("./disputes/DisputesRoutes"));
// Lazy import
const FundingRoutes = lazy(() => import("./funding/FundingRoutes"));
// Lazy import for named export
const PurchaseLinkRoutes = lazy(() =>
  import("./purchase-link").then((m) => ({ default: m.PurchaseLinkRoutes })),
);

const SubscriptionRoutes = lazy(() =>
  import("./subscription").then((m) => ({ default: m.SubscriptionRoutes })),
);

const InvoiceRoutes = lazy(() =>
  import("./invoices").then((m) => ({ default: m.InvoiceRoutes })),
);

// Lazy imports for named exports
const MembershipRoutes = lazy(() =>
  import("./membership").then((m) => ({ default: m.MembershipRoutes })),
);

const DonationRoutes = lazy(() =>
  import("./donation").then((m) => ({ default: m.DonationRoutes })),
);

const DashboardGrid = lazy(() =>
  import("../shared/styles").then((m) => ({ default: m.DashboardGrid })),
);

const DonationGridsMain = lazy(() =>
  import("./items").then((m) => ({ default: m.DonationGridsMain })),
);

const ItemRoutes = lazy(() =>
  import("./items").then((m) => ({ default: m.ItemRoutes })),
);

const ContactRoutes = lazy(() =>
  import("./contact").then((m) => ({ default: m.ContactRoutes })),
);

const OrderRoutes = lazy(() =>
  import("./orders").then((m) => ({ default: m.OrderRoutes })),
);

const PaymentRoutes = lazy(() =>
  import("./payment").then((m) => ({ default: m.PaymentRoutes })),
);

const PaymentSettingsRoutes = lazy(() =>
  import("./paymentSettings").then((m) => ({
    default: m.PaymentSettingsRoutes,
  })),
);

// Named export
const ServiceRoutes = lazy(() =>
  import("./services").then((m) => ({ default: m.ServiceRoutes })),
);

const HeaderBarMain = lazy(() =>
  import("./HeaderBarMain").then((m) => ({ default: m.default })),
);

// Named exports
const ProductRoutes = lazy(() =>
  import("./products").then((m) => ({ default: m.ProductRoutes })),
);

const PurchaseRoutes = lazy(() =>
  import("./purchase").then((m) => ({ default: m.PurchaseRoutes })),
);

const MerchantAccountRoutes = lazy(() =>
  import("./merchantAccounts").then((m) => ({
    default: m.MerchantAccountRoutes,
  })),
);

interface IProps {
  location: Location;
  navigate: NavigateFunction;
  itemStore: ItemsStore;
  rolesAndPermissionStore: RolesAndPermissionStore;
}

// MerchantAccountStore (singleton lives in MerchantAccountStore.ts)
export { getMerchantAccountStore };

// CheckoutStore
export const getCheckoutStore = (() => {
  let instance: CheckOutStore | null = null;
  return (): CheckOutStore => {
    if (!instance) {
      instance = new CheckOutStore();
    }
    return instance;
  };
})();

// PurchaseStore
export const getPurchaseStore = (() => {
  let instance: PurchaseStore | null = null;
  return (): PurchaseStore => {
    if (!instance) {
      instance = new PurchaseStore();
    }
    return instance;
  };
})();

// DashboardStore
export const getDashboardStore = (() => {
  let instance: DashboardStore | null = null;
  return (): DashboardStore => {
    if (!instance) {
      instance = new DashboardStore();
    }
    return instance;
  };
})();

const AppRoutes: FC<IProps> = ({
  location,
  itemStore,
  rolesAndPermissionStore,
}) => {
  const [merchantAccounts, setMerchantAccounts] = useState<MerchantAccount[]>(
    [],
  );
  const [isMerchantAccountLoading, setMerchantAccountLoading] = useState(true);
  const [selectedMerchantAccountId, setMerchantAccountId] =
    useState<string>("");
  useEffect(() => {
    if (location.pathname.includes("app/activity")) {
      getMerchantAccounts();
    } else {
      AuthService.setLastRoute(location.pathname);
    }
  }, [location]);

  // Keep the app-wide store in sync so shared chrome (left sidebar) can react
  // to the merchant account picked in the top bar.
  useEffect(() => {
    getMerchantAccountStore().setSelectedMerchantAccountId(
      selectedMerchantAccountId,
    );
  }, [selectedMerchantAccountId]);

  function hideHeader(): boolean {
    //if (location.pathname.includes("app/items")) return false;
    let isHide =
      location.pathname.includes("app/service") ||
      location.pathname.includes("app/product") ||
      location.pathname.includes("app/membership") ||
      location.pathname.includes("/app/checkout/") ||
      location.pathname.includes("/app/donation/") ||
      location.pathname.includes("purchase/checkout/") ||
      location.pathname.includes("purchase/purchase-link/");
    if (isHide) {
      //send message to parent window to hide header
      postMessageToParentWindow("hideHeader");
    } else {
      //send message to parent window to hide header
      postMessageToParentWindow("showHeader");
    }
    isHide = isHide || !AuthService.fromPaymentSystem;
    return isHide;
  }

  function postMessageToParentWindow(type: string) {
    if (!AuthService.fromPaymentSystem && window.parent) {
      addScriptToDocument();
      const { innerWidth: width, innerHeight: height } = window;
      const message = {
        type: type,
        width,
        height,
        showIframe: true,
      };
      window.parent.postMessage(JSON.stringify(message), "*");
    }
  }

  function addScriptToDocument() {
    const style = document.createElement("style");
    const contactDetailBackgroundColor = AuthService.isMindMeContactRoute
      ? "#ededed"
      : "";
    // Add custom styles
    style.textContent = `
     height: 100%;
background-color: #f4f7fb;
html {
  height: 100%;
  background-color: #f4f7fb;
}
body {
  height: 100%;
  background-color: #f4f7fb;
}
div#root {
  height: 100%;
  background-color: #f4f7fb;
}
iframe{
  background-color: #f4f7fb;
}
  .contact-routes-only {
    background-color: ${contactDetailBackgroundColor};
  }
    /* Add more custom styles here */
    `;
    // Append the style element to the head of the iframe's document
    document.head.appendChild(style);
  }

  function isPurchaseLinkPath(): boolean {
    return location.pathname.includes("purchase-link");
  }

  const getMerchantAccounts = () => {
    if (!AuthService.hasAccount) {
      setMerchantAccountLoading(false);
      return;
    }
    setMerchantAccountLoading(true);

    MerchantAccountService.getMerchantAccounts(
      AuthService.authData.accountId,
      1,
      10000,
      "",
    ).subscribe((response) => {
      if (response.result) {
        const allMerchantAccounts =
          response.result?.map((m) => MerchantAccount.deserializeAPIModel(m)) ||
          [];
        const activeMerchantAccounts = (
          response.result?.map((m) => MerchantAccount.deserializeAPIModel(m)) ||
          []
        ).filter((f) => f.applicationStatus === ApplicationStatus.Active);
        setMerchantAccounts(allMerchantAccounts);

        const defaultAccount = activeMerchantAccounts.find(
          (acc) => acc.isDefault,
        );
        if (activeMerchantAccounts.length > 0 && selectedMerchantAccountId) {
          const isSelectedActive = activeMerchantAccounts.some(
            (acc) => acc.merchantAccountId === selectedMerchantAccountId,
          );
          if (!isSelectedActive) {
            if (defaultAccount) {
              setMerchantAccountId(defaultAccount.merchantAccountId);
            } else if (activeMerchantAccounts.length > 0) {
              setMerchantAccountId(activeMerchantAccounts[0].merchantAccountId);
            }
          }
        } else {
          if (defaultAccount) {
            setMerchantAccountId(defaultAccount.merchantAccountId);
          } else {
            if (activeMerchantAccounts?.length > 0) {
              setMerchantAccountId(activeMerchantAccounts[0].merchantAccountId);
            }
          }
        }
      }
      setMerchantAccountLoading(false);
    });
    getMerchantAccountStore().fetchMerchantAccounts();
  };

  if (location.pathname.includes("app/activity")) {
    if (!isMerchantAccountLoading) {
      if (
        merchantAccounts.length > 0 &&
        !merchantAccounts.some(
          (x) => x.applicationStatus === ApplicationStatus.Active,
        ) &&
        !AuthService.fromPaymentSystem &&
        AuthService.hasAccount &&
        !AuthService.lastRoute //NOT having for last route means first route
      ) {
        return <Navigate to="/app/merchant-accounts" />;
      }
    } else {
      return null;
    }
  }
  // if (
  //   !isMerchantAccountLoading &&
  //   !AuthService.fromPaymentSystem &&
  //   !AuthService.lastRoute //NOT having for last route means first route
  // ) {
  //   const pendingMerchant = merchantAccounts.find(
  //     (m) => m.applicationStatus === ApplicationStatus.Pending
  //   );
  //   if (
  //     !location.pathname.includes("/app/merchant-accounts/pending") &&
  //     merchantAccounts.length === 1 &&
  //     pendingMerchant &&
  //     AuthService.hasAccount
  //   ) {
  //     return (
  //       <Navigate to={`/app/merchant-accounts/success/${pendingMerchant.id}`} />
  //     );
  //   }
  // }

  const selectedMerchantAccount = merchantAccounts?.find(
    (ma) => ma.merchantAccountId === selectedMerchantAccountId,
  );
  return (
    <Suspense>
      <Provider {...RootStore}>
        {!hideHeader() && AuthService.fromPaymentSystem && <HeaderBarMain />}
        <NoPermissionDialog
          showPopup={rolesAndPermissionStore.showPopup}
          title={rolesAndPermissionStore.title}
          message={rolesAndPermissionStore.message}
          togglePermissionPopup={(value) =>
            rolesAndPermissionStore.togglePermissionPopup(value)
          }
        />
        <Routes>
          <Route
            path={`/service/*`}
            element={
              <ServiceRoutes
                rolesAndPermissionStore={rolesAndPermissionStore}
                merchantAccountStore={getMerchantAccountStore()}
                countryDataLoaded={itemStore.countryStore.countryDataLoaded}
                countriesWithState={itemStore.countryStore.countriesWithState}
              />
            }
          />
          <Route
            path={`/product/*`}
            element={
              <ProductRoutes
                rolesAndPermissionStore={rolesAndPermissionStore}
                merchantAccountStore={getMerchantAccountStore()}
                countryStore={itemStore.countryStore}
              />
            }
          />
          <Route
            path={`/purchase/:type/*`}
            element={
              <PurchaseRoutes
                purchaseStore={getPurchaseStore()}
                isPurchaseLink={isPurchaseLinkPath()}
                rolesAndPermissionStore={rolesAndPermissionStore}
                merchantAccountStore={getMerchantAccountStore()}
                countryStore={itemStore.countryStore}
              />
            }
          />
          <Route
            path={`/checkout/*`}
            element={
              <CheckOutRoutes
                checkoutStore={getCheckoutStore()}
                countryStore={itemStore.countryStore}
                rolesAndPermissionStore={rolesAndPermissionStore}
                merchantAccountStore={getMerchantAccountStore()}
              />
            }
          />
          <Route
            path={`/checkout/donation/:id`}
            element={<CheckoutDonation checkoutStore={getCheckoutStore()} />}
          />
          <Route
            path={`/checkout/preview`}
            element={<CheckoutPreview countryStore={itemStore.countryStore} />}
          />
          <Route
            path={`/checkout/preview/detail`}
            element={<CheckoutPreviewForDetailPage />}
          />
          <Route
            path={`/checkouts/*`}
            element={
              <CheckoutsMain
                itemStore={itemStore}
                rolesAndPermissionStore={rolesAndPermissionStore}
                merchantAccountStore={getMerchantAccountStore()}
              />
            }
          />
          <Route
            path={`/membership/*`}
            element={
              <MembershipRoutes
                rolesAndPermissionStore={rolesAndPermissionStore}
                countryDataLoaded={itemStore.countryStore.countryDataLoaded}
                countriesWithState={itemStore.countryStore.countriesWithState}
                merchantAccountStore={getMerchantAccountStore()}
              />
            }
          />
          <Route
            path={`/donation/*`}
            element={
              <DonationRoutes
                rolesAndPermissionStore={rolesAndPermissionStore}
                merchantAccountStore={getMerchantAccountStore()}
                countryDataLoaded={itemStore.countryStore.countryDataLoaded}
                countriesWithState={itemStore.countryStore.countriesWithState}
              />
            }
          />
          <Route
            path={`/donations/*`}
            element={
              <DashboardGrid>
                <DonationGridsMain
                  itemStore={itemStore}
                  rolesAndPermissionStore={rolesAndPermissionStore}
                  merchantAccountStore={getMerchantAccountStore()}
                />
              </DashboardGrid>
            }
          />
          <Route
            path={`/items/*`}
            element={
              <ItemRoutes
                itemStore={itemStore}
                rolesAndPermissionStore={rolesAndPermissionStore}
                merchantAccountStore={getMerchantAccountStore()}
              />
            }
          />
          <Route
            path={`/contacts/*`}
            element={
              <DashboardGrid className="contact-routes-only">
                <ContactRoutes
                  itemStore={itemStore}
                  contactStore={itemStore.contactStore}
                  rolesAndPermissionStore={rolesAndPermissionStore}
                  merchantAccountStore={getMerchantAccountStore()}
                />
              </DashboardGrid>
            }
          />
          <Route
            path={`/activity/*`}
            element={
              <DashboardGrid>
                <DashboardMain
                  itemStore={itemStore}
                  merchantAccountId={selectedMerchantAccountId}
                  selectedMerchantAccount={selectedMerchantAccount}
                  merchantAccounts={merchantAccounts}
                  dashboardStore={getDashboardStore()}
                  onChangeMerchantAccount={(merchantAccountId: string) => {
                    setMerchantAccountId(merchantAccountId);
                  }}
                  isMerchantAccountLoading={isMerchantAccountLoading}
                  rolesAndPermissionStore={rolesAndPermissionStore}
                  merchantAccountStore={getMerchantAccountStore()}
                />
              </DashboardGrid>
            }
          />

          <Route
            path={`/orders/*`}
            element={
              <DashboardGrid>
                <OrderRoutes
                  itemStore={itemStore}
                  orderStore={itemStore.orderStore}
                  rolesAndPermissionStore={rolesAndPermissionStore}
                  merchantAccountStore={getMerchantAccountStore()}
                />
              </DashboardGrid>
            }
          />
          <Route
            path={`/funding/*`}
            element={
              <DashboardGrid>
                <FundingRoutes
                  selectedMerchantAccount={selectedMerchantAccount}
                  itemStore={itemStore}
                  fundingStore={itemStore.fundingStore}
                  rolesAndPermissionStore={rolesAndPermissionStore}
                  merchantAccountStore={getMerchantAccountStore()}
                />
              </DashboardGrid>
            }
          />
          <Route
            path={`/disputes/*`}
            element={
              <DashboardGrid>
                <DisputesRoutes
                  selectedMerchantAccount={selectedMerchantAccount}
                  itemStore={itemStore}
                  rolesAndPermissionStore={rolesAndPermissionStore}
                  merchantAccountStore={getMerchantAccountStore()}
                />
              </DashboardGrid>
            }
          />
          <Route
            path={`/merchant-accounts/*`}
            element={
              <MerchantAccountRoutes
                itemStore={itemStore}
                merchantAccountStore={getMerchantAccountStore()}
                rolesAndPermissionStore={rolesAndPermissionStore}
                getMerchantAccounts={() => getMerchantAccounts()}
              />
            }
          />
          <Route
            path={`/subscriptions/*`}
            element={
              <SubscriptionRoutes
                itemStore={itemStore}
                subscriptionStore={itemStore.subscriptionStore}
                rolesAndPermissionStore={rolesAndPermissionStore}
                merchantAccountStore={getMerchantAccountStore()}
              />
            }
          />
          <Route
            path={`/payments/*`}
            element={
              <DashboardGrid>
                <PaymentRoutes
                  itemStore={itemStore}
                  paymentStore={itemStore.paymentStore}
                  rolesAndPermissionStore={rolesAndPermissionStore}
                  merchantAccountStore={getMerchantAccountStore()}
                />
              </DashboardGrid>
            }
          />
          <Route
            path={`/invoices/*`}
            element={
              <DashboardGrid>
                <InvoiceRoutes
                  itemStore={itemStore}
                  invoiceStore={itemStore.invoiceStore}
                  rolesAndPermissionStore={rolesAndPermissionStore}
                  merchantAccountStore={getMerchantAccountStore()}
                />
              </DashboardGrid>
            }
          />
          <Route
            path={`/purchase-link/*`}
            element={
              <DashboardGrid>
                <PurchaseLinkRoutes
                  itemStore={itemStore}
                  rolesAndPermissionStore={rolesAndPermissionStore}
                  merchantAccountStore={getMerchantAccountStore()}
                />
              </DashboardGrid>
            }
          />
          <Route
            path="/payment-settings/*"
            element={
              <DashboardGrid>
                <PaymentSettingsRoutes
                  itemStore={itemStore}
                  rolesAndPermissionStore={rolesAndPermissionStore}
                  merchantAccountStore={getMerchantAccountStore()}
                ></PaymentSettingsRoutes>
              </DashboardGrid>
            }
          />
          <Route path="*" element={<Navigate to="/app/activity" />} />
        </Routes>
      </Provider>
    </Suspense>
  );
};

export default inject(
  "itemStore",
  "rolesAndPermissionStore",
)(withRouter(observer(AppRoutes)));
